import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { addDays, format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { CalendarCheck, CheckCircle2, Phone } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { BOOKING_SLOTS, BRAND } from "../data/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-[#234E33]/20 bg-[#FAF7F2] px-4 py-3 text-sm text-[#1A2E20] placeholder-[#4a544c]/50 outline-none transition-colors focus:border-[#2E6941]";

const STEPS = [
  ["Pick your slot", "Choose a date and two-hour window that suits you — visits run Monday to Saturday."],
  ["We confirm", "Chris confirms your visit by phone or email, usually within a few hours."],
  ["We walk the property", "A free on-site visit, honest advice, and a written quote within one business day."],
];

export default function Booking() {
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [booked, setBooked] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", notes: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!date) return;
    setSlot(null);
    axios
      .get(`${API}/bookings/slots`, { params: { date: format(date, "yyyy-MM-dd") } })
      .then((r) => setBooked(r.data.booked))
      .catch(() => setBooked([]));
  }, [date]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !slot) {
      toast.error("Pick a date and a time window first.");
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/bookings`, {
        ...form,
        date: format(date, "yyyy-MM-dd"),
        slot,
      });
      setDone({ dateLabel: r.data.date, slotLabel: r.data.slot });
      toast.success("Booked — a confirmation is on its way to your inbox.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : `Couldn't book right now — please call ${BRAND.phoneDisplay}.`);
      if (err.response?.status === 409 && date) {
        try {
          const r = await axios.get(`${API}/bookings/slots`, { params: { date: format(date, "yyyy-MM-dd") } });
          setBooked(r.data.booked);
          setSlot(null);
        } catch {}
      }
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setDone(null);
    setDate(null);
    setSlot(null);
    setForm({ name: "", email: "", phone: "", address: "", notes: "" });
  };

  return (
    <main className="bg-[#FBF9F5] pt-28 lg:pt-36" data-testid="booking-page">
      <section className="mx-auto max-w-7xl px-5 pb-20 lg:px-8 lg:pb-28">
        <Reveal>
          <Eyebrow>Free On-Site Quotes</Eyebrow>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-black tracking-tight text-[#1A2E20] sm:text-5xl lg:text-6xl">
            Book your free visit.
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#4a544c] sm:text-lg">
            Pick a time and Chris comes to you — a walk of the property, straight advice, and a written quote within
            one business day. No pressure, no obligation.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-5">
          <div className="space-y-4 lg:col-span-2">
            {STEPS.map(([title, text], i) => (
              <Reveal key={title} delay={i * 0.08}>
                <div className="flex gap-4 border border-[#234E33]/12 bg-[#FAF7F2] p-5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#2E6941]/10 font-display text-sm font-bold text-[#2E6941]">
                    {i + 1}
                  </span>
                  <div>
                    <p className="font-display text-lg font-bold text-[#1A2E20]">{title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-[#4a544c]">{text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="bg-[#1A2E20] p-6 text-[#FBF9F5]">
                <p className="font-display text-lg font-bold">Rather just call?</p>
                <p className="mt-2 text-sm text-[#FBF9F5]/65">
                  Reach us at{" "}
                  <a href={`tel:${BRAND.phone}`} className="font-semibold text-[#72B043] hover:underline">
                    {BRAND.phoneDisplay}
                  </a>{" "}
                  and we'll find a slot together.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-3">
            {done ? (
              <div className="flex h-full flex-col items-center justify-center border border-[#234E33]/12 bg-[#FAF7F2] p-10 text-center" data-testid="booking-success">
                <CheckCircle2 size={52} className="text-[#2E6941]" />
                <h2 className="mt-5 font-display text-3xl font-bold text-[#1A2E20]">You're booked.</h2>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-[#4a544c]">
                  <span className="font-semibold text-[#1A2E20]">{done.dateLabel}</span> ·{" "}
                  <span className="font-semibold text-[#1A2E20]">{done.slotLabel}</span>
                  <br />
                  A confirmation email is on its way. We'll call or email shortly to confirm the visit.
                </p>
                <button
                  type="button"
                  onClick={reset}
                  data-testid="booking-book-another-btn"
                  className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#1A2E20]/25 px-6 py-3 text-sm font-semibold text-[#1A2E20] transition-colors hover:border-[#2E6941] hover:text-[#2E6941]"
                >
                  Book another visit
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-[#234E33]/12 bg-[#FAF7F2] p-7 lg:p-10" data-testid="booking-form">
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                  <div>
                    <p className="font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#2E6941]">1 · Pick a date</p>
                    <div className="mt-3 flex justify-center border border-[#234E33]/15 bg-white p-2" data-testid="booking-calendar">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={[{ before: addDays(new Date(), 1) }, { dayOfWeek: [0] }]}
                        classNames={{
                          day_selected:
                            "bg-[#1A2E20] text-[#FBF9F5] hover:bg-[#2E6941] hover:text-[#FBF9F5] focus:bg-[#1A2E20] focus:text-[#FBF9F5]",
                          day_today: "bg-[#2E6941]/10 text-[#1A2E20] font-semibold",
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <p className="font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#2E6941]">2 · Pick a window</p>
                    <div className="mt-3 space-y-2">
                      {BOOKING_SLOTS.map((s) => {
                        const taken = booked.includes(s.id);
                        return (
                          <button
                            key={s.id}
                            type="button"
                            disabled={!date || taken}
                            onClick={() => setSlot(s.id)}
                            data-testid={`booking-slot-${s.id}`}
                            className={`flex w-full items-center justify-between border px-4 py-3 text-left text-sm transition-colors duration-200 ${
                              taken
                                ? "cursor-not-allowed border-[#234E33]/10 bg-[#234E33]/5 text-[#4a544c]/40 line-through"
                                : slot === s.id
                                  ? "border-[#2E6941] bg-[#2E6941]/10 font-semibold text-[#1A2E20]"
                                  : "border-[#234E33]/20 text-[#1A2E20]/80 hover:border-[#2E6941]"
                            } ${!date ? "cursor-not-allowed opacity-50" : ""}`}
                          >
                            {s.label}
                            {taken && <span className="text-[10px] uppercase tracking-widest no-underline">Taken</span>}
                          </button>
                        );
                      })}
                    </div>
                    {!date && (
                      <p className="mt-3 text-xs text-[#4a544c]/70">Select a date on the calendar to see availability.</p>
                    )}
                  </div>
                </div>

                <p className="mt-8 font-mono-x text-[10px] font-semibold uppercase tracking-[0.25em] text-[#2E6941]">3 · Your details</p>
                <div className="mt-3 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <input required value={form.name} onChange={set("name")} placeholder="Full name *" data-testid="booking-name-input" className={inputCls} />
                  <input required type="email" value={form.email} onChange={set("email")} placeholder="Email *" data-testid="booking-email-input" className={inputCls} />
                  <input required value={form.phone} onChange={set("phone")} placeholder="Phone *" data-testid="booking-phone-input" className={inputCls} />
                  <input required value={form.address} onChange={set("address")} placeholder="Property address *" data-testid="booking-address-input" className={inputCls} />
                </div>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={set("notes")}
                  placeholder="Anything we should know? (gate codes, dogs, trouble spots…)"
                  data-testid="booking-notes-input"
                  className={`${inputCls} mt-5`}
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="booking-submit-btn"
                  className="group mt-7 inline-flex items-center gap-2 rounded-full bg-[#1A2E20] px-8 py-4 text-sm font-semibold text-[#FBF9F5] transition-colors duration-200 hover:bg-[#2E6941] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <CalendarCheck size={16} />
                  {sending ? "Booking…" : "Confirm My Free Visit"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}
